import React, { createContext, useContext, useEffect, useState } from "react";
import UserService from "../services/UserService";
import userManagementService from "services/userManagementService";

declare global {
  interface Window {
    smartsupp: (action: string, ...args: any[]) => void;
  }
}

interface UserContextType {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  updateUserProfile: (
    firstName?: string | undefined,
    lastName?: string | undefined
  ) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userId, setUserId] = useState(UserService.getUserId() || "");
  const [firstName, setFirstName] = useState(UserService.getFirstName() || "");
  const [lastName, setLastName] = useState(UserService.getLastName() || "");
  const [email, setEmail] = useState(UserService.getEmail() || "");

  const updateUserProfile = async (
    newFirstName: string | undefined,
    newLastName: string | undefined
  ) => {
    try {
      await userManagementService.updateUserProfile(newFirstName, newLastName);
      setFirstName(newFirstName);
      setLastName(newLastName);
    } catch (error) {
      throw error;
    }
  };

  // Send user's name and email to SmartSupp
  useEffect(() => {
    if (!userId || !window.smartsupp) return;

    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) {
      window.smartsupp("name", fullName);
    }

    if (email) {
      window.smartsupp("email", email);
    }

    // Optional but recommended
    window.smartsupp("variable", "userId", userId);

  }, [userId, firstName, lastName, email]);

  return (
    <UserContext.Provider
      value={{
        userId,
        firstName,
        lastName,
        email,
        updateUserProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
